import React from 'react';
import { XMarkIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, BuildingStorefrontIcon, PhotoIcon, UserIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl } from '../../utils/formatters';

const BusinessReviewModal = ({ business, onClose, onApprove, onReject, onBlock, onDelete }) => {
  if (!business) return null;

  let businessDetails = business.business_details || {};
  if (typeof businessDetails === 'string') {
    try {
      businessDetails = JSON.parse(businessDetails);
    } catch (e) {
      console.error('Failed to parse business_details in BusinessReviewModal', e);
      businessDetails = {};
    }
  }

  const businessImages = businessDetails.business_images || [];

  const formatPhone = (phone) => {
    if (!phone) return 'Not provided';
    return phone;
  };

  const businessTypeLabels = {
    salon: 'Salon / Spa',
    clinic: 'Clinic / Medical',
    gym: 'Gym / Fitness',
    ride: 'Ride / Transportation'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">Review Business Application</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {/* Business Header */}
          <div className="mb-6">
            <div className="flex items-start gap-4">
              {businessDetails.business_profile_image ? (
                <img
                  src={resolveImageUrl(businessDetails.business_profile_image)}
                  alt={businessDetails.business_name}
                  className="w-20 h-20 rounded-lg object-cover border"
                />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center">
                  <BuildingStorefrontIcon className="h-10 w-10 text-gray-400" />
                </div>
              )}

              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900">{businessDetails.business_name || 'Business Name Not Provided'}</h3>
                <p className="text-lg text-gray-600">{businessTypeLabels[businessDetails.business_type] || businessDetails.business_type}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Registered on {new Date(business.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Business Owner Information */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Business Owner Information</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                {business.profile_image ? (
                  <img
                    src={resolveImageUrl(business.profile_image)}
                    alt={`${business.first_name || ''} ${business.last_name || ''}`.trim() || 'Owner'}
                    className="w-12 h-12 rounded-full object-cover border bg-white"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-white border flex items-center justify-center">
                    <UserIcon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-500">Owner</div>
                  <div className="text-gray-900 font-medium">{business.first_name} {business.last_name}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <p className="text-gray-900">{business.first_name} {business.last_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{business.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="text-gray-900">{formatPhone(business.phone)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">User Type</label>
                  <p className="text-gray-900 capitalize">{business.user_type?.replace('_', ' ') || 'Business Owner'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Business Details</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Business Name</label>
                  <p className="text-gray-900">{businessDetails.business_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Business Type</label>
                  <p className="text-gray-900">{businessTypeLabels[businessDetails.business_type] || businessDetails.business_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Business Email</label>
                  <p className="text-gray-900">{businessDetails.business_email || business.email || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Business Phone</label>
                  <p className="text-gray-900">{formatPhone(businessDetails.business_phone) || formatPhone(business.phone) || 'Not provided'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-900">{businessDetails.business_address || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Business Images */}
          {businessImages.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-4 text-gray-900">Business Images</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {businessImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={resolveImageUrl(image)}
                      alt={`Business image ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Information Summary */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Contact Information</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Business Email</p>
                    <p className="text-gray-900">{businessDetails.business_email || business.email || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Business Phone</p>
                    <p className="text-gray-900">{formatPhone(businessDetails.business_phone) || formatPhone(business.phone) || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Business Address</p>
                    <p className="text-gray-900">{businessDetails.business_address || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Application Status */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-900">Application Status</h4>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <span className="text-yellow-800 font-medium">Pending Admin Review</span>
              </div>
              <p className="text-yellow-700 text-sm mt-2">
                This business application is awaiting your approval. Please review all details carefully before making a decision.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={() => onBlock?.(business.id)}
            className="px-4 py-2 text-white bg-yellow-600 rounded-lg hover:bg-yellow-700"
          >
            Block Owner
          </button>
          <button
            onClick={() => onDelete?.(business.id)}
            className="px-4 py-2 text-white bg-gray-900 rounded-lg hover:bg-black"
          >
            Delete
          </button>
          <button
            onClick={() => onReject(business.id)}
            className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
          >
            Reject Application
          </button>
          <button
            onClick={() => onApprove(business.id)}
            className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
          >
            Approve Application
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessReviewModal;
